import { getApplicationPaths, gocclFetch } from '../app-context/accounts-context';
import { AgencyApiResponse, AgencyCommissionApiResponse, TermPeriod, AgencyType } from '../model/agency-api';
import { getJsonRequest } from '../helpers/ccl-fetch.helper';
import { LocalizedDate } from 'gocclr-shared-ui/dist/models';
import { AgencyTnCAcceptanceApiResponse } from '../model/agency-tnc-api';

export interface AgencyInfo {
    readonly agencyId: string;
    readonly agencyName: string;
    readonly agencyPhone: string;
    readonly agencyFullAddress: string;
    readonly agencyAddress1: string;
    readonly agencyAddress2: string;
    readonly agencyComplementaryAddress: string;
    readonly ownerFirstName: string;
    readonly ownerMiddleName: string;
    readonly ownerLastName: string;
}

export interface AgencyCommission {
    readonly agencyType?: AgencyType;
    readonly commissions?: Record<string, string[]>[];
    readonly termPeriod?: TermPeriod;
    readonly commYearLevel?: number;
    readonly error?: string;
}

export interface AddAgencyTermsAcceptancApiResponse {
    message: string;
}

export interface ValidateAgencyTermsAcceptancApiResponse {
    message: string;
}

export interface AgencyCommissionAccessApiResponse {
    hasAccess: boolean;
}

export interface AgencyTnCAcceptanceInfo {
    readonly agencyId: string,
    readonly firstName: string,
    readonly lastName: string,
    readonly role: string,
    readonly acceptanceDate: LocalizedDate,
    readonly version: number
}

export function getAgencyInfo(): Promise<AgencyInfo> {
    const paths = getApplicationPaths();

    return gocclFetch<AgencyApiResponse>(paths.agencyInfo, getJsonRequest('GET')).then(agencyApiResponse => {
        const agencyInfo: AgencyInfo = {
            agencyId: agencyApiResponse.agencyId,
            agencyName: agencyApiResponse.agencyName,
            agencyPhone: agencyApiResponse.agencyPhone,
            agencyFullAddress: agencyApiResponse.agencyFullAddress,
            agencyAddress1: agencyApiResponse.agencyAddress1,
            agencyAddress2: agencyApiResponse.agencyAddress2,
            agencyComplementaryAddress: agencyApiResponse.agencyComplementaryAddress,
            ownerFirstName: agencyApiResponse.ownerFirstName,
            ownerMiddleName: agencyApiResponse.ownerMiddleName,
            ownerLastName: agencyApiResponse.ownerLastName
        };

        return agencyInfo;
    });
}

export function getAgencyCommission(): Promise<AgencyCommission> {
    const paths = getApplicationPaths();

    return gocclFetch<AgencyCommissionApiResponse>(paths.agencyCommissions, getJsonRequest('GET')).then(agencyCommissionApiResponse => {
        const commissions: AgencyCommission = {
            agencyType: agencyCommissionApiResponse.agencyType,
            commissions: agencyCommissionApiResponse.commissions,
            termPeriod: agencyCommissionApiResponse.termPeriod,
            commYearLevel: agencyCommissionApiResponse.commYearLevel
        };

        return commissions;
    }).catch((error) => {
        const commissions: AgencyCommission = {
            error: error.code
        };

        return commissions;
    });
}

export function addAgencyTnCAcceptance(): Promise<AddAgencyTermsAcceptancApiResponse> {
    const paths = getApplicationPaths();

    return gocclFetch<AddAgencyTermsAcceptancApiResponse>(paths.addAgencyTermsAcceptance, getJsonRequest('POST'));
}

export function validateAgencyTnCAcceptance(): Promise<boolean> {
    const paths = getApplicationPaths();

    return gocclFetch<ValidateAgencyTermsAcceptancApiResponse>(paths.validateAgencyTermsAcceptance, getJsonRequest('GET'))
        .then(() => {
            return true;
        }).catch(() => {
            return false;
        });
}

export function validateAgencyCommissionAccess(): Promise<boolean> {
    const paths = getApplicationPaths();

    return gocclFetch<AgencyCommissionAccessApiResponse>(paths.validateAgencyCommissionAccess, getJsonRequest('GET'))
        .then((agencyCommissionAccessApiResponse) => {
            return !!agencyCommissionAccessApiResponse.hasAccess;
        }).catch(() => {
            return false;
        });
}

export function getAgencyTnCAcceptance(): Promise<AgencyTnCAcceptanceApiResponse> {
    const paths = getApplicationPaths();

    return gocclFetch<AgencyTnCAcceptanceApiResponse>(paths.getAgencyTermsAcceptance, getJsonRequest('GET')).then(agencyTnCApiResponse => {
        const agencyTnCInfo: AgencyTnCAcceptanceInfo = {
            agencyId: agencyTnCApiResponse.agencyId,
            firstName: agencyTnCApiResponse.firstName,
            lastName: agencyTnCApiResponse.lastName,
            role: agencyTnCApiResponse.role,
            acceptanceDate: agencyTnCApiResponse.acceptanceDate,
            version: agencyTnCApiResponse.version
        };

        return agencyTnCInfo;
    });
}